import { TRACKABLE_EVENTS, trackEventPRH } from 'helpers/trackerHelpers';
import {
    useQuery,
    useMutation,
    useQueryClient,
    UseQueryResult,
} from '@tanstack/react-query';
import { RecurrenceEnum } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import { SIMULATION_ENDPOINT_PREFIX } from 'routes/HumanResourcesModule/Simulation/helpers/constants';
import { resetSimulationEnvelopeDataQueries } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/NonRecurrentEnvelope/helpers/hooks/nonRecurrentEnvelope';
import { resetSimulationAgentDataQueries } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/helpers/hooks/agentsSimulation';
import { useHypothesesChangesContext } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/components/contexts/HypothesesChangesContext';
import { useEnvelopeMantyStatusHypothesesChangesContext } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/MantyStatusHypothesis/contexts/EnvelopeMantyStatusHypothesesChangesContext';
import { resetEnvelopeMantyStatusHypothesesQueries } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/MantyStatusHypothesis/helpers/hooks/mantyStatusHypothesis';
import { resetResultSimulationDataQueries } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/helpers/hooks/resultSimulationData';
import { RQAPICallWithData } from 'store/APIClient';
import { dispatch } from 'store/store';
import { resetAgentDepartureHypothesesQueries } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Departure/helpers/hooks/agentDepartureHypothesis';
import { resetAgentPayComponentHypothesesQueries } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/PayComponent/helpers/hooks/agentPayComponentHypothesis';
import { NonRecurrentHypothesisTypePerMantyStatus } from '../types';

const NON_RECURRENT_HYPOTHESIS_TYPE_PER_MANTY_STATUS_API_KEY =
    'non_recurrent_hypothesis_type_per_manty_status';

export const useGetNonRecurrentHypothesisTypePerMantyStatus = (
    hrSimulationId: number,
): UseQueryResult<NonRecurrentHypothesisTypePerMantyStatus> => {
    const endpoint = `${SIMULATION_ENDPOINT_PREFIX}/non_recurrent_hypothesis_type_per_manty_status/${hrSimulationId}`;

    return useQuery({
        queryKey: [
            NON_RECURRENT_HYPOTHESIS_TYPE_PER_MANTY_STATUS_API_KEY,
            hrSimulationId,
        ],
        queryFn: async () => {
            const getResponse =
                await RQAPICallWithData<NonRecurrentHypothesisTypePerMantyStatus>(
                    {
                        endpoint,
                        method: 'GET',
                    },
                );
            return getResponse.content.data;
        },
        meta: {
            useGlobalOnError: true,
        },
    });
};

/**
 * Custom hook to reset all context linked to the hypotheses changes.
 *
 * This hook provides a function to reset the state of various hypotheses contexts
 * to their initial state (i.e., no changes). To use this hook, the component
 * must be wrapped inside the corresponding context providers.
 *
 * @returns {Function} - A function that, when called, resets the state of
 *                       the linked hypotheses contexts.
 */
const useResetHypothesesChangedContext = () => {
    const { setHasChanges: setHasEnvelopeMantyStatusHypothesesChanges } =
        useEnvelopeMantyStatusHypothesesChangesContext();
    const { setHasChanges: setHasAgentHypothesesChanges } =
        useHypothesesChangesContext();

    const resetChanges = () => {
        setHasEnvelopeMantyStatusHypothesesChanges(false);
        setHasAgentHypothesesChanges(false);
    };

    return resetChanges;
};

export const usePatchNonRecurrentHypothesisTypePerMantyStatus = (
    hrSimulationId: number,
) => {
    const queryClient = useQueryClient();

    // This allows us to reset the "ApplyEvolutionButton" once we change the params
    const resetHypothesesChanges = useResetHypothesesChangedContext();

    return useMutation({
        mutationFn: (payload: NonRecurrentHypothesisTypePerMantyStatus) =>
            RQAPICallWithData<NonRecurrentHypothesisTypePerMantyStatus>({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/non_recurrent_hypothesis_type_per_manty_status/${hrSimulationId}`,
                method: 'PATCH',
                body: payload,
            }),

        onSuccess: (response) => {
            queryClient.setQueryData(
                [
                    NON_RECURRENT_HYPOTHESIS_TYPE_PER_MANTY_STATUS_API_KEY,
                    hrSimulationId,
                ],
                () => response.content.data,
            );
            dispatch.vision.notifyUser({
                message: response.content.message,
                snackType: 'success',
            });

            resetHypothesesChanges();

            // as we have reset hypotheses, we reload them
            resetAgentDepartureHypothesesQueries(hrSimulationId, queryClient);
            resetAgentPayComponentHypothesesQueries(
                hrSimulationId,
                queryClient,
            );
            resetEnvelopeMantyStatusHypothesesQueries(
                hrSimulationId,
                queryClient,
            );
            // as we have hypotheses changes, we reset the result queries
            resetResultSimulationDataQueries(queryClient, hrSimulationId);

            // as we have hypotheses changes, we reset the non recurrent agent simulation data queries
            resetSimulationAgentDataQueries(
                hrSimulationId,
                queryClient,
                RecurrenceEnum.NON_RECURRENT,
            );

            // as we have hypotheses changes, we reset the non recurrent envelope simulation data queries
            resetSimulationEnvelopeDataQueries(hrSimulationId, queryClient);

            trackEventPRH(
                TRACKABLE_EVENTS.human_resources.simulation.nonRecurrent
                    .applyHypothesesConfig,
                response.content.data,
            );
        },
        meta: { useGlobalOnError: true },
    });
};
